/* Urja — small shared behaviours for the urja-* sections. No dependencies. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Reveal on scroll ------------------------------------------------------ */
  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 })
    : null;

  const initReveal = (root = document) => {
    root.querySelectorAll('.urja-reveal:not(.is-visible)').forEach((el) => {
      if (!revealObserver || reduceMotion) el.classList.add('is-visible');
      else revealObserver.observe(el);
    });
  };

  /* <urja-carousel> --------------------------------------------------------
     Markup: <urja-carousel><div class="urja-carousel__track" data-track>...</div>
             <button data-prev>…</button><button data-next>…</button><div data-dots></div></urja-carousel>
     Optional attribute: autoplay="5000" (ms). */
  class UrjaCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.prev = this.querySelectorAll('[data-prev]');
      this.next = this.querySelectorAll('[data-next]');
      this.dotsEl = this.querySelector('[data-dots]');
      this.prev.forEach((b) => b.addEventListener('click', () => this.go(-1)));
      this.next.forEach((b) => b.addEventListener('click', () => this.go(1)));
      this.track.addEventListener('scroll', () => this.queueUpdate(), { passive: true });
      this.resizeObserver = new ResizeObserver(() => { this.buildDots(); this.update(); });
      this.resizeObserver.observe(this.track);
      const autoplay = parseInt(this.getAttribute('autoplay'), 10);
      if (autoplay && !reduceMotion) {
        this.timer = setInterval(() => { if (!this.matches(':hover, :focus-within')) this.go(1, true); }, autoplay);
      }
    }
    disconnectedCallback() { this.resizeObserver?.disconnect(); clearInterval(this.timer); }
    get items() { return Array.from(this.track.children).filter((el) => el.offsetParent !== null); }
    get pageCount() {
      const step = this.stepSize();
      if (!step) return 1;
      return Math.max(1, Math.round((this.track.scrollWidth - this.track.clientWidth) / step) + 1);
    }
    stepSize() {
      const items = this.items;
      if (items.length < 2) return this.track.clientWidth;
      return items[1].offsetLeft - items[0].offsetLeft;
    }
    currentIndex() { const step = this.stepSize(); return step ? Math.round(this.track.scrollLeft / step) : 0; }
    go(dir, loop = false) {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (loop && dir > 0 && this.track.scrollLeft >= max) { this.track.scrollTo({ left: 0 }); return; }
      this.track.scrollBy({ left: dir * this.stepSize() });
    }
    goTo(i) { this.track.scrollTo({ left: i * this.stepSize() }); }
    buildDots() {
      if (!this.dotsEl) return;
      const count = this.pageCount;
      if (this.dotsEl.childElementCount === count) return;
      this.dotsEl.innerHTML = '';
      this.dotsEl.hidden = count < 2;
      for (let i = 0; i < count; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'urja-carousel__dot';
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.addEventListener('click', () => this.goTo(i));
        this.dotsEl.appendChild(dot);
      }
    }
    queueUpdate() { cancelAnimationFrame(this.raf); this.raf = requestAnimationFrame(() => this.update()); }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      this.prev.forEach((b) => (b.disabled = this.track.scrollLeft <= 2));
      this.next.forEach((b) => (b.disabled = this.track.scrollLeft >= max));
      const idx = this.track.scrollLeft >= max ? this.pageCount - 1 : this.currentIndex();
      this.dotsEl?.querySelectorAll('.urja-carousel__dot').forEach((d, i) => d.setAttribute('aria-current', String(i === idx)));
      this.toggleAttribute('scrollable', max > 0);
    }
  }

  /* <urja-countup data-target="91" data-suffix="%"> ---------------------- */
  class UrjaCountup extends HTMLElement {
    connectedCallback() {
      const target = parseFloat(this.dataset.target || this.textContent) || 0;
      const suffix = this.dataset.suffix || '';
      const decimals = (this.dataset.target || '').split('.')[1]?.length || 0;
      const render = (v) => (this.textContent = v.toFixed(decimals) + suffix);
      if (reduceMotion || !('IntersectionObserver' in window)) return render(target);
      render(0);
      const io = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        const start = performance.now();
        const duration = parseInt(this.dataset.duration, 10) || 1600;
        const tick = (now) => {
          const p = Math.min(1, (now - start) / duration);
          render(target * (1 - Math.pow(1 - p, 3)));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }, { threshold: 0.25 });
      // Observe the nearest revealed container: the number itself may sit inside a clipped/animated bar.
      io.observe(this.closest('[data-countup-root], .urja-reveal') || this);
    }
  }

  /* <urja-tabs> — [role=tab][aria-controls] + [role=tabpanel] ------------- */
  class UrjaTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(e.key)) return;
          e.preventDefault();
          const n = this.tabs.length;
          const next = e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : (i + (e.key === 'ArrowRight' ? 1 : -1) + n) % n;
          this.select(next, true);
        });
      });
    }
    select(index, focus = false) {
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !active;
      });
      if (focus) this.tabs[index].focus();
      this.tabs[index].scrollIntoView({ block: 'nearest', inline: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
      this.dispatchEvent(new CustomEvent('urja:tab-change', { detail: { index }, bubbles: true }));
    }
  }

  /* <urja-marquee> — duplicates its .urja-marquee__inner content once ------ */
  class UrjaMarquee extends HTMLElement {
    connectedCallback() {
      const inner = this.querySelector('.urja-marquee__inner');
      if (!inner || inner.dataset.cloned) return;
      inner.dataset.cloned = 'true';
      Array.from(inner.children).forEach((child) => {
        const clone = child.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        inner.appendChild(clone);
      });
    }
  }

  /* <urja-delivery-date business-days="3" format="short"> — "Oct 18" ------ */
  class UrjaDeliveryDate extends HTMLElement {
    connectedCallback() {
      const add = (date, days) => {
        const d = new Date(date);
        let left = days;
        while (left > 0) {
          d.setDate(d.getDate() + 1);
          if (d.getDay() !== 0 && d.getDay() !== 6) left--;
        }
        return d;
      };
      const min = parseInt(this.getAttribute('business-days'), 10) || 0;
      const maxAttr = parseInt(this.getAttribute('business-days-max'), 10);
      const fmt = { weekday: this.hasAttribute('weekday') ? 'short' : undefined, month: 'short', day: 'numeric' };
      const locale = document.documentElement.lang || 'en-US';
      const now = new Date();
      const cutoff = parseInt(this.getAttribute('cutoff-hour'), 10);
      const start = !isNaN(cutoff) && now.getHours() >= cutoff ? add(now, 1) : now;
      const from = add(start, min).toLocaleDateString(locale, fmt);
      this.textContent = isNaN(maxAttr) ? from : `${from} – ${add(start, maxAttr).toLocaleDateString(locale, fmt)}`;
    }
  }

  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  define('urja-carousel', UrjaCarousel);
  define('urja-countup', UrjaCountup);
  define('urja-tabs', UrjaTabs);
  define('urja-marquee', UrjaMarquee);
  define('urja-delivery-date', UrjaDeliveryDate);

  initReveal();
  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));
})();
